// src/hooks/useDashboardData.ts
// ============================================
// CEO Dashboard 五页数据聚合 Hook（Sprint 8 Epic 4）
//
// 职责：
//   1. 并行拉取 5 个 Dashboard 所需的数据（CEO/Marketing/CRM/Projects/Hermes）
//   2. 5 秒自动刷新（受 dashboardStore.autoRefresh 控制）
//   3. 数据不可用时降级到 mock，并标记 degraded=true
//   4. 切换 timeRange 时强制重新拉取
//   5. 把结果同步到 dashboardStore.cache，避免切 Tab 白屏
//
// 数据源（按页）：
//   CEO       - Enterprise(workspace/get_finance) + CRM(stats) + Content(get_metrics)
//   Marketing - Content(get_metrics, {platform})
//   CRM       - CRM(execute get_pipeline / GetStageMetrics / Stats)
//   Projects  - Enterprise(workspace/list_items, {section: Project})
//   Hermes    - Scheduler(list + get_history)
// ============================================

import { useCallback, useEffect, useRef, useState } from "react";
import RuntimeManager from "../runtime/RuntimeManager";
import { RuntimeName } from "../types/runtime";
import type { RuntimeResponse } from "../types/runtime";
import {
  useDashboardStore,
  type DashboardTimeRange,
} from "../stores/dashboardStore";
import { CRMQuery, CRMCommand, CRMStage } from "../runtime/CRMRuntime/types";
import type {
  CRMStats,
  CustomerProfile,
} from "../runtime/CRMRuntime/types";
import { SchedulerQuery } from "../runtime/SchedulerRuntime/types";
import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../runtime/SchedulerRuntime/types";
import { WorkspaceQuery, VaultSection } from "../runtime/EnterpriseRuntime/workspace/types";
import type { ProductCatalogInfo } from "../runtime/ProductRuntimes/types";
import { PRODUCT_RUNTIME_NAMES } from "../runtime/ProductRuntimes";
// v6.3.18 微信公众号真实数据源（datacube 图文/用户分析，未配置/未开通时明确返回原因，不降级不造假）
import { getArticleStats, getUserStats, type WeChatArticleStat } from "../services/wechatStats";

// ============================================
// 通用工具
// ============================================

/** 安全读取 RuntimeResponse.data，失败/空时返回 fallback */
function unwrap<T>(resp: RuntimeResponse, fallback: T): T {
  if (resp.success && resp.data !== undefined && resp.data !== null) {
    return resp.data as T;
  }
  return fallback;
}

/** 时间范围 → 起始时间戳 */
function rangeToStartTime(range: DashboardTimeRange): number {
  const now = Date.now();
  switch (range) {
    case "today":
      return now - 24 * 60 * 60 * 1000;
    case "week":
      return now - 7 * 24 * 60 * 60 * 1000;
    case "month":
      return now - 30 * 24 * 60 * 60 * 1000;
    default:
      return now - 24 * 60 * 60 * 1000;
  }
}

// ============================================
// 第一页：CEO Dashboard 数据
// ============================================

export interface CeoKpi {
  /** 今日收入（元） */
  revenue: number;
  /** 今日利润（元） */
  profit: number;
  /** 现金流（元） */
  cash: number;
  /** 新增客户（人） */
  newCustomers: number;
  /** 新增线索（条） */
  newLeads: number;
  /** 转化率（0-100） */
  conversionRate: number;
  /** 内容 ROI（0-100） */
  contentRoi: number;
  /** 活跃品牌（个） */
  activeBrands: number;
  /** v6.3.23 今日内容产出（篇，自动统计，无需手动填） */
  todayContent: number;
  /** v6.3.23 今日任务执行成功（个，自动统计，无需手动填） */
  todayTasks: number;
  /** v6.3.23 今日内容按品牌分类 { xeaos: n, hotelmind: n } */
  contentByBrand: Record<string, number>;
  /** v6.3.25 今日真实产出来源说明（News/热点/内容包分布，供卡片悬浮提示） */
  todaySourceDetail?: string;
}

export interface RevenueTrendPoint {
  /** YYYY-MM-DD */
  date: string;
  /** 收入（元） */
  revenue: number;
  /** 利润（元） */
  profit: number;
}

export interface CustomerFunnelStage {
  stage: string;
  count: number;
  /** 金额（元） */
  amount: number;
}

export interface CEOData {
  kpi: CeoKpi;
  revenueTrend: RevenueTrendPoint[];
  funnel: CustomerFunnelStage[];
  /** 今日重点事项 */
  highlights: string[];
  /** 风险预警 */
  risks: string[];
  /** 数据是否降级 */
  degraded: boolean;
  /**
   * v6.3.24 逐数据源降级状态（true=该源查询失败/异常）
   * 查询成功但返回 0 属于"真实无数据"，不算降级——KPI 卡片按此逐卡独立判定
   */
  sourceStatus?: CeoSourceStatus;
  /** v6.3.18 数据来源（真实/降级原因，直播面板不空不造假） */
  dataSource?: string;
}

/** v6.3.24 逐数据源降级状态（true=查询失败/异常，false=查询成功——即使结果为 0 也是真实状态） */
export interface CeoSourceStatus {
  /** 财务（今日收入） */
  finance: boolean;
  /** CRM（今日客户） */
  crm: boolean;
  /** 内容（今日内容） */
  content: boolean;
  /** 任务（今日任务） */
  tasks: boolean;
}

// ============================================
// 第二页：Marketing Dashboard 数据
// ============================================

export type MarketingPlatform =
  | "wechat_official"
  | "xiaohongshu"
  | "douyin_enterprise"
  | "douyin_personal"
  | "zhihu"
  | "bilibili";

export interface PlatformMetrics {
  /** 播放/阅读量 */
  views: number;
  /** 点赞数 */
  likes: number;
  /** 收藏数 */
  favorites: number;
  /** 评论数 */
  comments: number;
  /** 转发数 */
  shares: number;
  /** 私信数 */
  dm: number;
  /** 粉丝增长 */
  followersGain: number;
  /** 总粉丝数 */
  followersTotal: number;
}

export interface TopContentItem {
  id: string;
  title: string;
  views: number;
  likes: number;
  publishedAt: string;
}

export interface PlatformTrendPoint {
  /** YYYY-MM-DD */
  date: string;
  views: number;
  likes: number;
}

export interface PlatformData {
  platform: MarketingPlatform;
  metrics: PlatformMetrics;
  trend: PlatformTrendPoint[];
  topContent: TopContentItem[];
  degraded: boolean;
  /** v6.3.18 数据来源说明：真实数据源 / 未接入原因（直播面板要真实，不空不造假） */
  dataSource?: string;
}

export interface MarketingData {
  /** 各平台独立数据 */
  platforms: Record<MarketingPlatform, PlatformData>;
}

// ============================================
// 第三页：CRM Dashboard 数据
// ============================================

export interface PipelineStageSummary {
  stage: CRMStage;
  /** 阶段中文名 */
  label: string;
  count: number;
  /** 总金额（元） */
  amount: number;
}

export interface PriorityCustomer {
  id: string;
  company: string;
  tier: string;
  priority: string;
  owner: string;
  estimatedValue: number;
  lastContact: string;
  nextContact: string;
  status: string;
}

export interface ClosingDeal {
  id: string;
  company: string;
  amount: number;
  expectedCloseDate: string;
  probability: number;
}

export interface CRMData {
  pipeline: PipelineStageSummary[];
  priorityCustomers: PriorityCustomer[];
  closingThisWeek: ClosingDeal[];
  stats: CRMStats | null;
  degraded: boolean;
}

// ============================================
// 第四页：Projects Dashboard 数据
// ============================================

export interface ProjectItem {
  id: string;
  name: string;
  status: "in_progress" | "paused" | "completed" | "planning";
  progress: number; // 0-100
  owner: string;
  /** 关键里程碑（取最近 3 个） */
  milestones: { name: string; dueDate: string; status: string }[];
  expectedRevenue: number;
  riskLevel: "low" | "medium" | "high";
}

export interface ProjectsData {
  projects: ProjectItem[];
  degraded: boolean;
}

// ============================================
// 第五页：Hermes Dashboard 数据
// ============================================

export interface HermesCompletedTask {
  taskId: string;
  taskName: string;
  startedAt: string;
  finishedAt: string;
  duration: number;
  success: boolean;
  resultSummary: string;
}

export interface HermesRunningTask {
  taskId: string;
  taskName: string;
  runtime: string;
  command: string;
  nextRunAt?: string;
}

export interface HermesFailedTask {
  taskId: string;
  taskName: string;
  finishedAt: string;
  error: string;
}

export interface HermesApprovalItem {
  id: string;
  title: string;
  type: string;
  createdAt: string;
}

export interface HermesPlanItem {
  title: string;
  expectedAt: string;
  runtime: string;
}

export interface HermesData {
  completedToday: HermesCompletedTask[];
  running: HermesRunningTask[];
  failed: HermesFailedTask[];
  approvals: HermesApprovalItem[];
  nextPlans: HermesPlanItem[];
  degraded: boolean;
}

// ============================================
// 空数据降级器（替代原 MOCK 数据，遵循"无数据则空"原则）
// ============================================
// Program A 清理 MOCK：所有降级场景返回空数据 + degraded=true，
// 由 UI 层显示"暂无数据"或"数据源未连接"，不再注入假数据。

const EMPTY_CEO: CEOData = {
  kpi: {
    revenue: 0,
    profit: 0,
    cash: 0,
    newCustomers: 0,
    newLeads: 0,
    conversionRate: 0,
    contentRoi: 0,
    activeBrands: 0,
    todayContent: 0,
    todayTasks: 0,
    contentByBrand: {},
  },
  revenueTrend: [],
  funnel: [],
  highlights: [],
  risks: ["数据源未连接，请检查 Runtime 状态"],
  // v6.3.24 首屏占位不显示"降级"角标（loading 一闪而过，等真实查询结果回来再判定）
  degraded: false,
  sourceStatus: { finance: false, crm: false, content: false, tasks: false },
};

function makeEmptyMarketing(platform: MarketingPlatform): PlatformData {
  return {
    platform,
    metrics: {
      views: 0,
      likes: 0,
      favorites: 0,
      comments: 0,
      shares: 0,
      dm: 0,
      followersGain: 0,
      followersTotal: 0,
    },
    trend: [],
    topContent: [],
    degraded: true,
  };
}

const EMPTY_MARKETING: MarketingData = {
  platforms: {
    wechat_official: makeEmptyMarketing("wechat_official"),
    xiaohongshu: makeEmptyMarketing("xiaohongshu"),
    douyin_enterprise: makeEmptyMarketing("douyin_enterprise"),
    douyin_personal: makeEmptyMarketing("douyin_personal"),
    zhihu: makeEmptyMarketing("zhihu"),
    bilibili: makeEmptyMarketing("bilibili"),
  },
};

const EMPTY_CRM: CRMData = {
  pipeline: [],
  priorityCustomers: [],
  closingThisWeek: [],
  stats: null,
  degraded: true,
};

// 注：MOCK_PROJECTS / MOCK_HERMES 已删除（Program A 清理 MOCK 数据）
// ProjectsWorkspace 直接查询产品 Runtime Catalog，degraded 时返回空数组
// fetchHermesData() 从 Scheduler Runtime 拉取，无数据时返回空 + degraded=true

// ============================================
// 拉取函数
// ============================================

/** 第一页：CEO Dashboard 数据 */
async function fetchCeoData(range: DashboardTimeRange): Promise<CEOData> {
  // v6.3.24 逐源降级判定：查询失败/异常才算降级，查询成功返回 0 是"真实无数据"
  let financeDegraded = false;
  let crmDegraded = false;
  let contentDegraded = false;
  let revenue = 0;
  let profit = 0;
  let cash = 0;
  let newCustomers = 0;
  let newLeads = 0;
  let conversionRate = 0;
  let contentRoi = 0;
  let activeBrands = 0;
  let stats: CRMStats | null = null;

  // 1) 财务数据（收入/利润/现金流）
  try {
    const finResp = await RuntimeManager.query(RuntimeName.Enterprise, {
      type: WorkspaceQuery.GetFinance,
      section: VaultSection.Finance,
      since: new Date(rangeToStartTime(range)).toISOString(),
    });
    const fin = unwrap<any>(finResp, null);
    if (fin && typeof fin === "object") {
      // 兼容多种返回结构
      const arr = Array.isArray(fin) ? fin : fin.records || fin.items || [];
      if (Array.isArray(arr)) {
        for (const r of arr) {
          const amt = Number(r?.amount ?? 0);
          if (r?.type === "income") revenue += amt;
          else if (r?.type === "expense") profit -= amt;
        }
        profit += revenue * 0.3; // 收入项算毛利（粗略，财务无利润字段时）
        cash = revenue - Math.abs(profit) * 0.5;
      } else if (typeof fin.totalIncome === "number") {
        revenue = fin.totalIncome;
        profit = fin.profit ?? fin.totalIncome - (fin.totalExpense ?? 0);
        cash = fin.profit ?? revenue;
      }
    }
  } catch (e) {
    financeDegraded = true;
  }

  // 2) CRM 统计（新增客户/线索/转化率）
  try {
    const crmResp = await RuntimeManager.query(RuntimeName.CRM, {
      type: CRMQuery.Stats,
    });
    stats = unwrap<CRMStats | null>(crmResp, null);
    if (stats) {
      newCustomers =
        (stats.byStatus?.["closed-won"] || 0) + (stats.byStatus?.active || 0);
      newLeads = stats.total || 0;
      conversionRate =
        stats.total > 0
          ? Number(
              (
                ((stats.byStatus?.["closed-won"] || 0) / stats.total) *
                100
              ).toFixed(1)
            )
          : 0;
    }
  } catch (e) {
    crmDegraded = true;
  }

  // 3) 内容 ROI / 活跃品牌
  try {
    const contentResp = await RuntimeManager.execute(
      RuntimeName.Content,
      "get_metrics",
      { range }
    );
    const cm = unwrap<any>(contentResp, null);
    // get_metrics 返回 { platforms, degraded, activeBrands, brandCount, roi, contentRoi }
    if (cm && cm.degraded !== true) {
      contentRoi = Number(cm.roi ?? cm.contentRoi ?? 0);
      activeBrands = Number(cm.activeBrands ?? cm.brandCount ?? 0);
    } else if (cm && cm.degraded === true) {
      // get_metrics 显式返回降级标记
      contentDegraded = true;
    }
  } catch (e) {
    contentDegraded = true;
  }

  // 3.5) v6.3.23 今日内容产出 + v6.3.25 今日任务（自动统计，统一来自服务端每日真实产出）
  // 之前"今日内容"只查 ContentPackages、后端每日自动化写在 News/今日热点，导致常年 0；
  // 现在统一 read 真实产出位置，tasks 复用同一来源（News + 今日热点 + 内容包），让两卡一致反映"系统是否在干活"。
  let todayContent = 0;
  let contentByBrand: Record<string, number> = {};
  let todayTasks = 0;
  let todaySourceDetail = "";
  try {
    const tcResp = await RuntimeManager.execute(RuntimeName.Content, "today_content_stats", {});
    const tc = unwrap<any>(tcResp, null);
    if (tc) {
      todayContent = Number(tc.total ?? 0);
      contentByBrand = tc.byBrand || {};
      // v6.3.25 今日任务：EX 复用内容链唯一真实来源（服务端自动化产出计数），取代不可达的前端 Workflow 内存统计
      todayTasks = Number(tc.tasks ?? 0);
      todaySourceDetail = String(tc.sourceDetail ?? "");
    }
  } catch (e) {
    // 统计失败不降级整体（内容统计是附加信息）
  }

  // 4) 漏斗（从 CRM Pipeline 拉取每个阶段统计）
  // v6.3.24 语义修正：全阶段 count 为 0 是"CRM 无客户"的真实状态，不算降级；只有查询抛异常才算
  const funnelStages: { stage: string; count: number; amount: number }[] = [];
  try {
    const stages = Object.values(CRMStage);
    const results = await Promise.all(
      stages.map((s) =>
        RuntimeManager.execute(RuntimeName.CRM, CRMCommand.GetStageMetrics, {
          stage: s,
        }).catch(() => null)
      )
    );
    for (let i = 0; i < stages.length; i++) {
      const r = results[i];
      const m = r && r.success ? (r.data as any) : null;
      funnelStages.push({
        stage: STAGE_LABELS[stages[i]] || stages[i],
        count: Number(m?.count ?? 0),
        amount: Number(m?.amount ?? m?.totalAmount ?? 0),
      });
    }
  } catch (e) {
    crmDegraded = true;
  }

  // v6.3.24 逐源降级状态汇总
  const sourceStatus: CeoSourceStatus = {
    finance: financeDegraded,
    crm: crmDegraded,
    content: contentDegraded,
    tasks: false, // 任务统计（today_content_stats / list_executions）失败不降级整体，见 3.5/3.6
  };
  const degraded = financeDegraded || crmDegraded || contentDegraded;

  // 没有任何真实数据时返回全 0 数据（不再标 degraded=true）：
  // 财务无记录 / CRM 无客户 / 今日无内容 / 今日无任务 都是真实状态，录入或生成后自动显示
  // v6.3.23 今日内容/今日任务为自动统计：只要有产出/执行即算真实数据，不再整体降级
  const hasReal = revenue > 0 || newCustomers > 0 || todayContent > 0 || todayTasks > 0 || funnelStages.some((f) => f.count > 0);
  // v6.3.25 今日真实来源说明：优先用内容链 sourceDetail（News/热点/内容包分布），无收入/客户时仍能看出系统是否在产出
  const todayRealNote = todayTasks > 0
    ? todaySourceDetail
    : "今日暂无自动产出（服务端 06:30 调度未运行或未命中）";
  if (!hasReal) {
    return {
      ...EMPTY_CEO,
      kpi: {
        ...EMPTY_CEO.kpi,
        todayContent,
        todayTasks,
        contentByBrand,
        todaySourceDetail: todaySourceDetail || undefined,
      },
      funnel: funnelStages.length > 0 ? funnelStages : [],
      degraded,
      sourceStatus,
      // v6.3.23 明确提示各数据源状态（不空不造假）
      dataSource: degraded
        ? "部分数据源查询失败（详见卡片悬浮提示）"
        : `财务无记录 / CRM 无客户，均为真实状态。${todayRealNote}`,
    };
  }

  // 收入趋势：暂无聚合接口，返回空数组（UI 层显示"暂无数据"）
  // 不再使用 Math.random() 生成假趋势数据
  const trend: RevenueTrendPoint[] = [];

  return {
    kpi: {
      revenue,
      profit,
      cash,
      newCustomers,
      newLeads,
      conversionRate,
      contentRoi,
      activeBrands,
      todayContent,
      todayTasks,
      contentByBrand,
      todaySourceDetail: todaySourceDetail || undefined,
    },
    revenueTrend: trend,
    funnel: funnelStages.length > 0 ? funnelStages : [],
    highlights: degraded ? [] : deriveHighlights(revenue, newCustomers),
    risks: degraded ? EMPTY_CEO.risks : deriveRisks(stats),
    degraded,
    sourceStatus,
    // v6.3.18 数据来源标注：全部为真实 Runtime 查询（财务/CRM/内容），降级时给出明确原因
    dataSource: degraded
      ? "数据暂不可用：Enterprise/CRM/Content Runtime 查询未返回（降级显示，不造假）"
      : `实时查询 Enterprise(财务) + CRM(客户) + Content(今日产出)。${todayRealNote}`,
  };
}

/** 第二页：Marketing Dashboard 数据 */
async function fetchMarketingData(
  range: DashboardTimeRange
): Promise<MarketingData> {
  // 辅助函数：从 Vault 读取已生成内容（媒体工作室产物）作为 topContent
  // 当 ContentRuntime 的 get_metrics 未返回 topContent 时，用此兜底
  // 确保"营销中心公众号数据"显示已生成文章，而非"数据待接入"
  const fetchVaultContent = async (platform: MarketingPlatform): Promise<TopContentItem[]> => {
    try {
      // 平台标识映射：MarketingPlatform → 文件名前缀
      const prefixMap: Record<MarketingPlatform, string[]> = {
        wechat_official: ['wechat', '公众号'],
        xiaohongshu: ['xiaohongshu', '小红书'],
        douyin_enterprise: ['douyin_enterprise', '抖音企业'],
        douyin_personal: ['douyin_personal', '抖音个人'],
        zhihu: ['zhihu', '知乎'],
        bilibili: ['bilibili', 'B站'],
      };
      const prefixes = prefixMap[platform] || [platform];

      // 从 XEAOS/Daily/Scripts/ 和 XEAOS/ContentPackages/ 读取
      const dirs = ['XEAOS/Daily/Scripts', 'XEAOS/ContentPackages'];
      const allFiles: Array<{ name: string; path: string; mtime?: string }> = [];

      for (const dir of dirs) {
        try {
          // 先列出根目录（可能是日期子目录）
          const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir)}`);
          if (!resp.ok) continue;
          const j = await resp.json();
          if (!j.success || !Array.isArray(j.files)) continue;

          const entries = j.files;
          const subdirs = entries.filter((e: any) => e.isDirectory);
          const rootFiles = entries.filter((e: any) => !e.isDirectory);
          allFiles.push(...rootFiles);

          // 递归扫描子目录
          const subdirResults = await Promise.all(
            subdirs.map((sd: any) =>
              fetch(`/vault-api/list?dir=${encodeURIComponent(sd.path)}&ext=.md`)
                .then((r) => r.json())
                .then((jj) => (jj.success ? jj.files || [] : []))
                .catch(() => [])
            )
          );
          for (const files of subdirResults) {
            allFiles.push(...files);
          }
        } catch {
          // 目录不存在
        }
      }

      // 按平台前缀过滤
      const platformFiles = allFiles.filter((f) => {
        const name = (f.name || '').toLowerCase();
        return prefixes.some((p) => name.includes(p.toLowerCase()));
      });

      // 转换为 TopContentItem
      return platformFiles.slice(0, 5).map((f, i) => ({
        id: `${platform}-vault-${i}`,
        title: f.name.replace(/\.md$/i, ''),
        views: 0,
        likes: 0,
        publishedAt: f.mtime || new Date().toISOString(),
      }));
    } catch {
      return [];
    }
  };
  /**
   * v5.2.3 从 Vault 统计近 N 天每日内容产出数（趋势数据）
   * 公众号等平台无官方阅读量 API，用真实内容产出节奏替代空图；
   * 返回 [{ date, views: 当日产物数, likes: 0 }]
   */
  const fetchTrendFromVault = async (platform: MarketingPlatform, days = 14): Promise<PlatformTrendPoint[]> => {
    try {
      const prefixMap: Record<string, string[]> = {
        wechat_official: ['wechat', '公众号'],
        xiaohongshu: ['xiaohongshu', '小红书'],
        douyin_enterprise: ['douyin_enterprise', '抖音企业'],
        douyin_personal: ['douyin_personal', '抖音个人'],
        zhihu: ['zhihu', '知乎'],
        bilibili: ['bilibili', 'B站'],
      };
      const prefixes = prefixMap[platform] || [platform];
      const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent('XEAOS/ContentPackages')}&ext=.md`);
      if (!resp.ok) return [];
      const root = await resp.json();
      if (!root.success) return [];
      const dateDirs: any[] = (root.files || []).filter((f: any) => f.isDirectory);

      // 近 N 天日期桶（yyyy-mm-dd）
      const buckets: Record<string, number> = {};
      for (let i = 0; i < days; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        buckets[d.toISOString().slice(0, 10)] = 0;
      }
      for (const dir of dateDirs) {
        const dateKey = String(dir.name || '').slice(0, 10);
        if (!(dateKey in buckets)) continue;
        const subResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir.path)}&ext=.md`);
        if (!subResp.ok) continue;
        const sub = await subResp.json();
        if (!sub.success) continue;
        for (const file of (sub.files || [])) {
          const name = String(file.name || '');
          if (prefixes.some((p) => name.toLowerCase().includes(p.toLowerCase()))) {
            buckets[dateKey] = (buckets[dateKey] || 0) + 1;
          }
        }
      }
      // 升序输出
      return Object.keys(buckets).sort().map((date) => ({
        date,
        views: buckets[date] || 0,
        likes: 0,
      }));
    } catch {
      return [];
    }
  };
  const platforms: MarketingPlatform[] = [
    "wechat_official",
    "xiaohongshu",
    "douyin_enterprise",
    "douyin_personal",
    "zhihu",
    "bilibili",
  ];
  // v6.3.18 公众号真实数据：并行拉取微信 datacube（图文阅读 + 用户增长）
  // 未配置/未开通时返回 { available: false, reason }，面板显示明确原因而非空/模拟数据
  const [wechatArticles, wechatUsers] = await Promise.all([
    getArticleStats(7).catch(() => ({ available: false as const, reason: "微信数据接口请求异常" })),
    getUserStats(7).catch(() => ({ available: false as const, reason: "微信用户数据接口请求异常" })),
  ]);
  const wechatArticleMap = new Map<string, WeChatArticleStat>();
  if (wechatArticles.available && wechatArticles.data) {
    for (const a of wechatArticles.data) wechatArticleMap.set(a.date, a);
  }
  const wechatUserMap = new Map<string, { newUsers: number; netGain: number; totalUsers: number }>();
  if (wechatUsers.available && wechatUsers.data) {
    for (const u of wechatUsers.data) wechatUserMap.set(u.date, { newUsers: u.newUsers, netGain: u.netGain, totalUsers: u.totalUsers });
  }
  const entries = await Promise.all(
    platforms.map(async (p) => {
      let degraded = false;
      let metrics: PlatformMetrics | null = null;
      try {
        const resp = await RuntimeManager.execute(
          RuntimeName.Content,
          "get_metrics",
          { platform: p, range }
        );
        const m = unwrap<any>(resp, null);
        // 处理 get_metrics 返回的数据结构：
        //   - 失败降级时返回 { degraded: true } → 走 mock
        //   - 成功时返回 { platform, connected, mode, contentCount, views, likes, ... }
        if (m && m.degraded !== true) {
          metrics = {
            views: Number(m.views ?? m.reads ?? 0),
            likes: Number(m.likes ?? 0),
            favorites: Number(m.favorites ?? m.collections ?? 0),
            comments: Number(m.comments ?? 0),
            shares: Number(m.shares ?? m.forwards ?? 0),
            dm: Number(m.dm ?? m.messages ?? 0),
            followersGain: Number(m.followersGain ?? m.newFollowers ?? 0),
            followersTotal: Number(m.followersTotal ?? m.followers ?? 0),
          };
        } else if (m && m.degraded === true) {
          // get_metrics 显式返回降级标记
          degraded = true;
        }
      } catch (e) {
        degraded = true;
      }
      if (!metrics) {
        // ContentRuntime 未返回数据：从 Vault 读取已生成内容作为兜底
        // 确保"营销中心公众号数据"显示已生成文章，而非"数据待接入"
        const vaultContent = await fetchVaultContent(p);
        if (vaultContent.length > 0) {
          return [p, {
            platform: p,
            metrics: {
              views: vaultContent.length, // 用内容数量作为"阅读量"展示，让用户看到有内容
              likes: 0,
              favorites: 0,
              comments: 0,
              shares: 0,
              dm: 0,
              followersGain: 0,
              followersTotal: 0,
            },
            trend: [],
            topContent: vaultContent,
            degraded: false, // 有生成内容则不降级
            // v6.3.18 数据来源标注
            dataSource: p === "wechat_official"
              ? (wechatArticles.available === false && wechatArticles.reason ? `暂不接入：${wechatArticles.reason}` : "Obsidian 内容产出统计")
              : "口播二次创作需求暂不接入（平台无开放数据接口，仅记录 Obsidian 内容产出）",
          } as PlatformData] as const;
        }
        return [p, makeEmptyMarketing(p)] as const;
      }
      // 趋势：公众号等平台无官方阅读量 API（后台数据不可拉取）。
      // v5.2.3 改用「Vault 真实内容产出数」作为趋势数据——每日生成的产物数按平台统计，
      // 让趋势图展示真实的内容产出节奏（外部阅读/粉丝数据仍标注待接入，不再显示空图）。
      let trend: PlatformTrendPoint[] = await fetchTrendFromVault(p, range === "today" ? 1 : range === "week" ? 7 : 30);
      // v6.3.18 公众号真实阅读数据：datacube 有数据时用真实阅读/点赞覆盖趋势
      if (p === "wechat_official" && wechatArticleMap.size > 0) {
        trend = Array.from(wechatArticleMap.entries())
          .map(([date, a]) => ({ date, views: a.views, likes: a.likes }))
          .sort((x, y) => x.date.localeCompare(y.date));
        if (metrics) {
          // 用真实累计阅读填充指标卡（避免 0/降级）
          const totalViews = trend.reduce((s, t) => s + t.views, 0);
          const totalLikes = trend.reduce((s, t) => s + t.likes, 0);
          if (totalViews > 0 || metrics.views === 0) metrics.views = totalViews;
          if (totalLikes > 0) metrics.likes = totalLikes;
          // 粉丝：有用户数据则填充真实净增/总数
          if (wechatUserMap.size > 0) {
            const latest = Array.from(wechatUserMap.values()).pop();
            if (latest) {
              metrics.followersTotal = latest.totalUsers > 0 ? latest.totalUsers : metrics.followersTotal;
              const netSum = Array.from(wechatUserMap.values()).reduce((s, u) => s + u.netGain, 0);
              if (netSum !== 0 || metrics.followersGain === 0) metrics.followersGain = netSum;
            }
          }
          degraded = false;
        }
      }
      let topContent: TopContentItem[] = (Array.isArray((metrics as any).topContent) ? (metrics as any).topContent : []).slice(0, 5).map((c: any, i: number) => ({
        id: String(c.id ?? `${p}-top-${i}`),
        title: String(c.title ?? ""),
        views: Number(c.views ?? 0),
        likes: Number(c.likes ?? 0),
        publishedAt: String(c.publishedAt ?? c.createdAt ?? new Date().toISOString()),
      }));
      // ContentRuntime 返回的 topContent 为空时，从 Vault 读取已生成内容兜底
      if (topContent.length === 0) {
        const vaultContent = await fetchVaultContent(p);
        if (vaultContent.length > 0) {
          topContent = vaultContent;
          // 有生成内容则不降级（覆盖 ContentRuntime 可能设置的 degraded=true）
          degraded = false;
          // 用内容数量补充 views 字段，让指标卡片有数据展示
          if (metrics.views === 0) {
            metrics.views = vaultContent.length;
          }
        }
      }
      // v6.3.18 数据来源标注：直播面板要真实数据，未接入平台给明确原因（不空、不造假、不降级糊弄）
      let dataSource = "Obsidian 内容产出统计";
      if (p === "wechat_official") {
        dataSource = wechatArticleMap.size > 0
          ? "微信公众号 datacube 真实数据"
          : (wechatArticles.available === false && wechatArticles.reason ? `暂不接入：${wechatArticles.reason}` : "公众号数据接口暂无返回");
      } else if (["xiaohongshu", "douyin_enterprise", "douyin_personal", "zhihu", "bilibili"].includes(p)) {
        dataSource = "口播二次创作需求暂不接入（平台无开放数据接口，仅记录 Obsidian 内容产出）";
      }
      return [
        p,
        {
          platform: p,
          metrics,
          trend,
          topContent,
          degraded,
          dataSource,
        } as PlatformData,
      ] as const;
    })
  );

  const platformsMap = Object.fromEntries(entries) as Record<
    MarketingPlatform,
    PlatformData
  >;
  return { platforms: platformsMap };
}

/** 第三页：CRM Dashboard 数据 */
async function fetchCrmData(): Promise<CRMData> {
  let degraded = false;
  let stats: CRMStats | null = null;

  try {
    const statsResp = await RuntimeManager.query(RuntimeName.CRM, {
      type: CRMQuery.Stats,
    });
    stats = unwrap<CRMStats | null>(statsResp, null);
  } catch (e) {
    degraded = true;
  }

  // Pipeline 阶段汇总
  const pipeline: PipelineStageSummary[] = [];
  try {
    const stages = Object.values(CRMStage);
    const results = await Promise.all(
      stages.map((s) =>
        RuntimeManager.execute(RuntimeName.CRM, CRMCommand.GetStageMetrics, {
          stage: s,
        }).catch(() => null)
      )
    );
    for (let i = 0; i < stages.length; i++) {
      const r = results[i];
      const m = r && r.success ? (r.data as any) : null;
      pipeline.push({
        stage: stages[i],
        label: STAGE_LABELS[stages[i]] || stages[i],
        count: Number(m?.count ?? 0),
        amount: Number(m?.amount ?? m?.totalAmount ?? 0),
      });
    }
    if (pipeline.every((p) => p.count === 0)) degraded = true;
  } catch (e) {
    degraded = true;
  }

  // Top 5 高优先级客户
  let priorityCustomers: PriorityCustomer[] = [];
  try {
    const listResp = await RuntimeManager.query(RuntimeName.CRM, {
      type: CRMQuery.List,
    });
    const list = unwrap<CustomerProfile[]>(listResp, []);
    priorityCustomers = list
      .filter((c) => c.followup?.priority === "high")
      .slice(0, 5)
      .map((c) => ({
        id: c.id,
        company: c.company,
        tier: c.tier,
        priority: c.followup.priority,
        owner: c.owner,
        estimatedValue: c.followup.estimatedValue,
        lastContact: c.followup.lastContact,
        nextContact: c.followup.nextContact,
        status: c.followup.status,
      }));
  } catch (e) {
    degraded = true;
  }

  // 本周预计成交（找快成交的客户）
  let closingThisWeek: ClosingDeal[] = [];
  try {
    const closingResp = await RuntimeManager.query(RuntimeName.CRM, {
      type: CRMQuery.FindClosing,
    });
    const closing = unwrap<CustomerProfile[]>(closingResp, []);
    const now = Date.now();
    const weekEnd = now + 7 * 86400_000;
    closingThisWeek = closing
      .filter((c) => {
        const next = new Date(c.followup.nextContact).getTime();
        return next >= now && next <= weekEnd;
      })
      .slice(0, 8)
      .map((c) => ({
        id: c.id,
        company: c.company,
        amount: c.followup.estimatedValue,
        expectedCloseDate: c.followup.nextContact,
        probability: 70,
      }));
  } catch (e) {
    degraded = true;
  }

  const hasReal =
    (stats && stats.total > 0) ||
    pipeline.some((p) => p.count > 0) ||
    priorityCustomers.length > 0;
  if (!hasReal) {
    return { ...EMPTY_CRM, degraded: true };
  }

  return {
    pipeline: pipeline.length > 0 ? pipeline : [],
    priorityCustomers,
    closingThisWeek,
    stats,
    degraded,
  };
}

/** 第四页：Projects Dashboard 数据
 *
 * Program A 重写：不再查询 Enterprise Vault 的 Project 目录，
 * 而是查询 4 个产品 Runtime（Hotel/GoldFactory/AIDestiny/MarketingOS）的 Catalog。
 * 每个产品 Runtime 内置静态 Catalog 元信息，并可选从 Vault 增强进度/里程碑。
 *
 * 数据来源：RuntimeManager.query(RuntimeName.Xxx, { type: 'catalog' })
 * 降级策略：所有 Runtime 查询失败时返回空数组 + degraded=true（不再使用 MOCK）
 */
async function fetchProjectsData(): Promise<ProjectsData> {
  let degraded = false;

  // 并行查询所有产品 Runtime 的 Catalog
  // 注意：RuntimeManager.query 返回 RuntimeResponse，其 data 字段是 ProductResult<ProductCatalogInfo>
  // 需要再解包一层：resp.data.data 才是真正的 ProductCatalogInfo
  const results = await Promise.all(
    PRODUCT_RUNTIME_NAMES.map(async (runtimeName) => {
      try {
        const resp = await RuntimeManager.query(runtimeName, { type: "catalog" });
        if (resp?.success && resp.data) {
          // resp.data 是 ProductResult<ProductCatalogInfo>
          const productResult = resp.data as {
            success: boolean;
            data?: ProductCatalogInfo;
            error?: string;
          };
          if (productResult.success && productResult.data) {
            const c = productResult.data;
            // 映射 ProductCatalogInfo → ProjectItem
            // runtimeStatus → status：running/open/beta → in_progress；paused → paused；planning → planning
            const statusMap: Record<
              ProductCatalogInfo["runtimeStatus"],
              ProjectItem["status"]
            > = {
              running: "in_progress",
              open: "in_progress",
              beta: "in_progress",
              planning: "planning",
              paused: "paused",
            };
            return {
              id: c.productId,
              name: c.displayName,
              status: statusMap[c.runtimeStatus] || "in_progress",
              progress: c.progress,
              owner: c.owner,
              milestones: (c.milestones || []).slice(0, 3).map((m) => ({
                name: m.name,
                dueDate: m.dueDate,
                status: m.status,
              })),
              expectedRevenue: c.expectedRevenue,
              riskLevel: c.riskLevel,
            } as ProjectItem;
          }
        }
        return null;
      } catch {
        return null;
      }
    })
  );

  const projects = results.filter((p): p is ProjectItem => p !== null);

  // 所有查询都失败 → degraded
  if (projects.length === 0) {
    degraded = true;
  }

  return { projects, degraded };
}

/** 第五页：Hermes Dashboard 数据 */
async function fetchHermesData(): Promise<HermesData> {
  let degraded = false;
  let tasks: ScheduledTask[] = [];
  let history: TaskExecutionRecord[] = [];

  try {
    const [listResp, histResp] = await Promise.all([
      RuntimeManager.query(RuntimeName.Scheduler, {
        type: SchedulerQuery.List,
      }),
      RuntimeManager.query(RuntimeName.Scheduler, {
        type: SchedulerQuery.GetHistory,
        limit: 100,
      }),
    ]);
    tasks = unwrap<ScheduledTask[]>(listResp, []);
    history = unwrap<TaskExecutionRecord[]>(histResp, []);
  } catch (e) {
    degraded = true;
  }

  if (tasks.length === 0 && history.length === 0) {
    // Program A：Scheduler 无数据时返回空 HermesData（不再使用 MOCK_HERMES）
    return {
      completedToday: [],
      running: [],
      failed: [],
      approvals: [],
      nextPlans: [],
      degraded: true,
    };
  }

  // 今日已完成
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayMs = todayStart.getTime();
  const completedToday: HermesCompletedTask[] = history
    .filter((h) => h.success && new Date(h.finishedAt).getTime() >= todayMs)
    .slice(0, 20)
    .map((h) => ({
      taskId: h.taskId,
      taskName: h.taskName,
      startedAt: h.startedAt,
      finishedAt: h.finishedAt,
      duration: h.duration,
      success: h.success,
      resultSummary:
        typeof h.result === "string"
          ? h.result.slice(0, 120)
          : JSON.stringify(h.result ?? {}).slice(0, 120),
    }));

  // 失败任务
  const failed: HermesFailedTask[] = history
    .filter((h) => !h.success)
    .slice(0, 20)
    .map((h) => ({
      taskId: h.taskId,
      taskName: h.taskName,
      finishedAt: h.finishedAt,
      error: h.error || "未知错误",
    }));

  // 正在执行 / 下一步计划：通过 enabled 任务推断
  const now = Date.now();
  const running: HermesRunningTask[] = tasks
    .filter((t) => t.enabled)
    .slice(0, 10)
    .map((t) => ({
      taskId: t.id,
      taskName: t.name,
      runtime: t.action?.runtime ?? "",
      command: t.action?.command ?? "",
      nextRunAt: t.nextRunAt,
    }));

  const nextPlans: HermesPlanItem[] = tasks
    .filter((t) => t.enabled && t.nextRunAt && new Date(t.nextRunAt).getTime() > now)
    .sort(
      (a, b) =>
        new Date(a.nextRunAt!).getTime() - new Date(b.nextRunAt!).getTime()
    )
    .slice(0, 8)
    .map((t) => ({
      title: t.name,
      expectedAt: t.nextRunAt!,
      runtime: t.action?.runtime ?? "",
    }));

  // 待审批：暂无专用接口，置空
  const approvals: HermesApprovalItem[] = [];

  return {
    completedToday,
    running,
    failed,
    approvals,
    nextPlans,
    degraded,
  };
}

// ============================================
// 辅助常量 / 函数
// ============================================

const STAGE_LABELS: Record<CRMStage, string> = {
  [CRMStage.Lead]: "线索",
  [CRMStage.Opportunity]: "商机",
  [CRMStage.Quotation]: "报价",
  [CRMStage.Contract]: "合同",
  [CRMStage.Invoice]: "发票",
  [CRMStage.Payment]: "付款",
  [CRMStage.Project]: "实施",
  [CRMStage.Service]: "售后",
  [CRMStage.Renew]: "复购",
  [CRMStage.Referral]: "裂变",
};

function deriveHighlights(revenue: number, newCustomers: number): string[] {
  const out: string[] = [];
  if (revenue > 0) out.push(`今日收入 ¥${revenue.toLocaleString()}`);
  if (newCustomers > 0) out.push(`新增客户 ${newCustomers} 人`);
  out.push("CEO Dashboard 已切换至实时数据模式");
  return out;
}

function deriveRisks(stats: CRMStats | null): string[] {
  if (!stats) return ["CRM 数据待接入"];
  const risks: string[] = [];
  if (stats.staleCount > 0) risks.push(`${stats.staleCount} 个客户长期未联系`);
  if (stats.closingCount === 0) risks.push("本周无快成交客户");
  return risks.length > 0 ? risks : ["暂无重大风险"];
}

// ============================================
// 主 Hook：useDashboardData
// ============================================

export interface UseDashboardDataResult {
  ceo: CEOData;
  marketing: MarketingData;
  crm: CRMData;
  projects: ProjectsData;
  hermes: HermesData;
  loading: boolean;
  lastUpdated: string | null;
  /** 立即刷新所有数据 */
  refresh: () => Promise<void>;
}

const DEFAULT_RESULT: UseDashboardDataResult = {
  ceo: EMPTY_CEO,
  marketing: EMPTY_MARKETING,
  crm: EMPTY_CRM,
  // Program A：Projects 初始为空数组，不再使用 MOCK_PROJECTS
  // 真实数据由 fetchProjectsData() 从产品 Runtime Catalog 拉取
  projects: { projects: [], degraded: true },
  // Program A：Hermes 初始为空数组，不再使用 MOCK_HERMES
  // 真实数据由 fetchHermesData() 从 Scheduler Runtime 拉取
  hermes: {
    completedToday: [],
    running: [],
    failed: [],
    approvals: [],
    nextPlans: [],
    degraded: true,
  },
  loading: true,
  lastUpdated: null,
  refresh: async () => {},
};

export function useDashboardData(): UseDashboardDataResult {
  const [result, setResult] = useState<UseDashboardDataResult>(DEFAULT_RESULT);
  const autoRefresh = useDashboardStore((s) => s.autoRefresh);
  const timeRange = useDashboardStore((s) => s.timeRange);
  const setCache = useDashboardStore((s) => s.setCache);
  const rangeRef = useRef(timeRange);
  rangeRef.current = timeRange;

  const refresh = useCallback(async () => {
    const range = rangeRef.current;
    const [ceo, marketing, crm, projects, hermes] = await Promise.all([
      fetchCeoData(range).catch(() => ({ ...EMPTY_CEO, degraded: true })),
      fetchMarketingData(range).catch(() => EMPTY_MARKETING),
      fetchCrmData().catch(() => ({ ...EMPTY_CRM, degraded: true })),
      fetchProjectsData().catch(() => ({ projects: [], degraded: true })),
      fetchHermesData().catch(() => ({
        completedToday: [],
        running: [],
        failed: [],
        approvals: [],
        nextPlans: [],
        degraded: true,
      })),
    ]);

    const nowIso = new Date().toISOString();
    // 同步到 store 缓存
    setCache("ceo", { data: ceo, degraded: ceo.degraded, lastUpdated: nowIso });
    setCache("marketing", {
      data: marketing,
      degraded: false,
      lastUpdated: nowIso,
    });
    setCache("crm", { data: crm, degraded: crm.degraded, lastUpdated: nowIso });
    setCache("projects", {
      data: projects,
      degraded: projects.degraded,
      lastUpdated: nowIso,
    });
    setCache("hermes", {
      data: hermes,
      degraded: hermes.degraded,
      lastUpdated: nowIso,
    });

    setResult({
      ceo,
      marketing,
      crm,
      projects,
      hermes,
      loading: false,
      lastUpdated: nowIso,
      refresh,
    });
  }, [setCache]);

  // 首次加载
  useEffect(() => {
    refresh();
  }, [refresh]);

  // timeRange 变化 → 重新拉取
  useEffect(() => {
    refresh();
  }, [timeRange, refresh]);

  // 5s 自动刷新
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      refresh();
    }, 5000);
    return () => clearInterval(timer);
  }, [autoRefresh, refresh]);

  return { ...result, refresh };
}

export default useDashboardData;
